import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';
import Lottie from 'lottie-react';
import crayonAnim from '../../../public/json_file/Crayon.json';
import livreAnim from '../../../public/json_file/Livre ouvert.json';
import garconAnim from '../../../public/json_file/Garcon.json';
import footballAnim from '../../../public/json_file/Football.json';
import basketballAnim from '../../../public/json_file/Basketball.json';
import ideeAnim from '../../../public/json_file/Idée ampoule.json';
import calculatriceAnim from '../../../public/json_file/Calculatrice.json';
import globeAnim from '../../../public/json_file/Globe terrestre.json';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'block' }, // Desktop only
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFF7E5 0%, #FFE8C5 100%)',
        position: 'relative',
        overflow: 'hidden',
        pt: 12,
        pb: 0,
        px: 0,
        mx: 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(235, 164, 91, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 80% 50%, rgba(235, 164, 91, 0.08) 0%, transparent 50%)',
          pointerEvents: 'none',
        }
      }}
    >
      <Box
        sx={{
          maxWidth: '1400px',
          mx: 'auto',
          px: 6,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 6,
            minHeight: '60vh',
          }}
        >
          {/* Contenu texte - Gauche */}
          <Box
            sx={{
              flex: { md: '0 1 620px', lg: '0 1 640px' },
              maxWidth: { md: '640px', lg: '680px' },
              width: '100%',
              animation: `${slideInLeft} 1s ease-out`,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontFamily: 'Achiko',
                fontSize: { md: '3.5rem', lg: '4.5rem' },
                fontWeight: 'bold',
                color: '#2C3E50',
                mb: 3,
                lineHeight: 1.2,
                maxWidth: '100%',
                animation: `${fadeInUp} 1s ease-out`,
              }}
            >
              Accompagnez le{' '}
              <Box
                component="span"
                sx={{
                  color: '#784e3c',
                  display: 'inline-block',
                  animation: `${pulse} 2s ease-in-out infinite`,
                }}
              >
                développement
              </Box>
              <br />
              de votre enfant
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: '#5A6C7D',
                mb: 3,
                lineHeight: 1.6,
                fontSize: { md: '1.25rem', lg: '1.5rem' },
                maxWidth: { md: '560px', lg: '600px' },
                animation: `${fadeInUp} 1s ease-out 0.2s both`,
              }}
            >
              Une plateforme éducative innovante conçue pour stimuler la créativité et l&apos;apprentissage avec des activités adaptées au profil unique de chaque enfant.
            </Typography>

            {/* <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                mb: 4,
                p: 2.5,
                maxWidth: { md: '560px', lg: '600px' },
                borderRadius: 3,
                bgcolor: 'rgba(255, 255, 255, 0.65)',
                border: '1px solid rgba(120, 78, 60, 0.2)',
                animation: `${fadeInUp} 1s ease-out 0.3s both`,
              }}
            >
              <Chip
                label="Nouveau"
                size="small"
                sx={{
                  mt: 0.25,
                  fontWeight: 700,
                  bgcolor: '#FFD88C',
                  color: '#5a3d2b',
                  flexShrink: 0,
                }}
              />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#784e3c', fontWeight: 700, mb: 0.5, fontFamily: 'Achiko' }}
                >
                  Profils Neuroatypiques
                </Typography>
                <Typography variant="body2" sx={{ color: '#5A6C7D', lineHeight: 1.6 }}>
                  Un questionnaire bienveillant de 15 questions pour orienter chaque enfant vers la Valise VALED
                  adaptée — dynamisme, sensoriel et apprentissage, sans diagnostic médical.
                </Typography>
              </Box>
            </Box> */}

            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
                animation: `${fadeInUp} 1s ease-out 0.4s both`,
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/Profilling')}
                sx={{
                  backgroundColor: '#784e3c',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  px: 4,
                  py: 1.5,
                  borderRadius: '50px',
                  textTransform: 'none',
                  boxShadow: '0px 8px 20px rgba(235, 164, 91, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#D89440',
                    transform: 'translateY(-3px)',
                    boxShadow: '0px 12px 30px rgba(235, 164, 91, 0.6)',
                  },
                }}
              >
                Créer un profil enfant
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/articles')}
                sx={{
                  color: '#784e3c',
                  borderColor: '#784e3c',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  px: 4,
                  py: 1.5,
                  borderRadius: '50px',
                  textTransform: 'none',
                  borderWidth: '2px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#D89440',
                    backgroundColor: 'rgba(235, 164, 91, 0.1)',
                    borderWidth: '2px',
                    transform: 'translateY(-3px)',
                  },
                }}
              >
                Découvrir nos activités
              </Button>
            </Box>

            {/* Stats ou features */}
            <Box
              sx={{
                display: 'flex',
                gap: 4,
                mt: 6,
                animation: `${fadeInUp} 1s ease-out 0.6s both`,
              }}
            >
              {[
                { number: '50+', label: 'Activités' },
              //  { number: '10K+', label: 'Familles' },
                { number: '98%', label: 'Satisfaction' },
              ].map((stat, index) => (
                <Box key={index} sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'Achiko',
                      color: '#784e3c',
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#5A6C7D',
                      fontSize: '0.9rem',
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Lottie Animations - Droite */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              animation: `${slideInRight} 1s ease-out`,
              minHeight: '500px',
            }}
          >
            {/* Animation principale - Garçon */}
            <Box
              sx={{
                position: 'relative',
                width: '350px',
                height: '350px',
                animation: `${float} 3s ease-in-out infinite`,
                zIndex: 3,
              }}
            >
              <Lottie animationData={garconAnim} loop={true} />
            </Box>

            {/* Crayon - Top Left */}
            <Box
              sx={{
                position: 'absolute',
                top: '5%',
                left: '8%',
                width: '80px',
                height: '80px',
                animation: `${float} 2.5s ease-in-out infinite`,
                animationDelay: '0.3s',
                zIndex: 2,
              }}
            >
              <Lottie animationData={crayonAnim} loop={true} />
            </Box>

            {/* Livre - Top Right */}
            <Box
              sx={{
                position: 'absolute',
                top: '10%',
                right: '5%',
                width: '90px',
                height: '90px',
                animation: `${float} 3s ease-in-out infinite`,
                animationDelay: '0.8s',
                zIndex: 2,
              }}
            >
              <Lottie animationData={livreAnim} loop={true} />
            </Box>

            {/* Football - Bottom Left */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '15%',
                left: '2%',
                width: '70px',
                height: '70px',
                animation: `${float} 2s ease-in-out infinite`,
                animationDelay: '1.2s',
                zIndex: 1,
              }}
            >
              <Lottie animationData={footballAnim} loop={true} />
            </Box>

            {/* Basketball - Bottom Right */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '20%',
                right: '10%',
                width: '65px',
                height: '65px',
                animation: `${float} 2.8s ease-in-out infinite`,
                animationDelay: '0.5s',
                zIndex: 1,
              }}
            >
              <Lottie animationData={basketballAnim} loop={true} />
            </Box>

            {/* Idée Ampoule - Middle Left */}
            <Box
              sx={{
                position: 'absolute',
                top: '45%',
                left: '-5%',
                width: '75px',
                height: '75px',
                animation: `${pulse} 2s ease-in-out infinite`,
                zIndex: 2,
              }}
            >
              <Lottie animationData={ideeAnim} loop={true} />
            </Box>

            {/* Calculatrice - Middle Right */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                right: '-8%',
                width: '85px',
                height: '85px',
                animation: `${float} 3.5s ease-in-out infinite`,
                animationDelay: '1.5s',
                zIndex: 1,
              }}
            >
              <Lottie animationData={calculatriceAnim} loop={true} />
            </Box>

            {/* Globe - Bottom Center */}
            <Box
              sx={{
                position: 'absolute',
                bottom: '5%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '95px',
                height: '95px',
                animation: `${float} 4s ease-in-out infinite`,
                animationDelay: '0.2s',
                zIndex: 1,
              }}
            >
              <Lottie animationData={globeAnim} loop={true} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;
